/**
 * Which of the four dashboard views he is in.
 *
 * ONE module owns the key, the default and the list, so the picker, the router in MoDashboard and
 * the tests cannot disagree about what a view is called.
 *
 * The key is NEW, `mo-os:dashboard-view`, and the old `mo-os:dashboard-shape` is deliberately
 * never read. Reading it would carry a stale `deck` forward and the new default would reach
 * nobody who had ever opened the app. MoDashboard's own comment about the poisoned `mo-os:shape`
 * key is the precedent; this is the third key for the same reason twice.
 *
 * The default depends on WIDTH. He asked for the wall by default and he also said the phone view
 * is covered; a wall composed for an office screen is not what a 390px phone should open on. Below
 * DEFAULT_WIDE the deck stays the default. One number, his to change.
 */
import type { IconName } from './Icon.js';

export type DeckView = 'live' | 'work' | 'deck' | 'tower';

export const VIEW_KEY = 'mo-os:dashboard-view';
/** Viewport width, in CSS px, at and above which the wall is the default. */
export const DEFAULT_WIDE = 1100;

const VIEWS: readonly DeckView[] = ['live', 'work', 'deck', 'tower'];

export function isDeckView(v: unknown): v is DeckView {
  return typeof v === 'string' && (VIEWS as readonly string[]).includes(v);
}

type Store = Pick<Storage, 'getItem' | 'setItem'>;

export function readView(storage: Store | undefined, width: number): DeckView {
  const fallback: DeckView = width >= DEFAULT_WIDE ? 'live' : 'deck';
  try {
    const v = storage?.getItem(VIEW_KEY);
    return isDeckView(v) ? v : fallback;
  } catch {
    // Private mode can throw on read. A layout preference is not worth a blank page.
    return fallback;
  }
}

/** Written ONLY from a press. A default that writes itself down stops being a default. */
export function writeView(storage: Store | undefined, v: DeckView): void {
  try {
    storage?.setItem(VIEW_KEY, v);
  } catch {
    // It will simply not be remembered.
  }
}

export const VIEW_ROWS: readonly { key: DeckView; label: string; note: string; icon: IconName }[] = [
  { key: 'live', label: 'Live wall', note: 'the room view, default on a wide screen', icon: 'agent' },
  { key: 'work', label: 'Work board', note: 'queues, lists and the next step', icon: 'action' },
  { key: 'deck', label: 'Command Deck', note: 'the board he approved', icon: 'graph' },
  { key: 'tower', label: 'Tracking tower', note: 'every panel, one page', icon: 'judgement' },
];
